import React, { useRef, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import Icona from 'components/3catinfo/ui/Icona';
import { useScript } from 'hooks/useScript';
import Mosaic from '../Mosaic/Mosaic';
import __isUndefined from 'lodash/isUndefined';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import styles from './slider.module.scss';

const buildSlide = (component, i) => {
    const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    const key = component.key || generateUniqueId();

    const stylesSwiperSlide = clsx({
        swiperSlideMenu: true,
        [styles.swiperSlideMenuItem]: true
    });

    return (
        <SwiperSlide key={`slider-menu-${key}`} tag='li' className={stylesSwiperSlide}>
            {component}
        </SwiperSlide>
    );
};

const iterateItems = (items) => {
    return !__isEmpty(items) ? Array.from(items).map((component, i) => buildSlide(component, i)) : null;
};

const SliderMenu = (props) => {
    const { className, estilFletxes, seccio, spaceBetween, fons } = props;
    const sliderId = 'idMenuSeccio';
    const { isJavaScriptClientEnabled } = useScript();
    const [allowSlideNext, setAllowSlideNext] = useState(true);
    const [allowSlidePrev, setAllowSlidePrev] = useState(false);
    const items = props.children;
    const swiperRef = useRef(null);
    const [seccioActual, setSeccioActual] = useState(seccio);
    let resizeTimeOut;

    const stylesSwiper = clsx({
        [styles.swiperMenu]: true,
        [className]: !!className
    });

    const stylesMosaicNoJavascript = clsx({
        [styles.mosaicNoJavascript]: true,
        [styles.sliderMenu]: true,
        [styles[`columns-${items.length}`]]: !!items,
        [className]: !!className
    });

    const stylesFletxes = clsx({
        [styles.contentFletxesMenu]: true,
        [styles[`fletxes-mig`]]: true,
        [styles[estilFletxes]]: !!estilFletxes,
        [styles[fons]]: !!fons
    });

    const stylesIconaFletxa = clsx({
        [styles.iconaFletxaMenu]: true,
        [styles.fletxaMig]: true
    });

    const stylesButtonNext = clsx({
        [styles.fonsFletxesMenuSeguent]: true,
        [styles.disabled]: !allowSlideNext
    });

    const stylesButtonPrev = clsx({
        [styles.fonsFletxesMenuAnterior]: true,
        [styles.disabled]: !allowSlidePrev
    });

    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleClickedSlide();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seccio]);

    if (__isUndefined(props.children) || props.children.length <= 0) return <></>;

    const handlePlaceSlider = (index) => {
        const swiperInstance = swiperRef.current?.swiper;
        let offset = 30;
        clearTimeout(resizeTimeOut);
        resizeTimeOut = setTimeout(() => {
            swiperInstance.slideTo(index, 0);
            swiperInstance.slides[index].focus();
            if (index > 0 && index < swiperInstance.slides.length - 1) {
                swiperInstance.setTranslate(swiperInstance.translate + offset, 0);
            }
            let nextPosition;
            if (index >= swiperInstance.slides.length - 1) {
                nextPosition = swiperInstance.snapGrid[swiperInstance.snapGrid.length - 1];
                nextPosition = nextPosition * -1 - 66;
                swiperInstance.setTranslate(nextPosition, 0);
            }
            swiperInstance.update();
        }, 100);
    };

    const handleWindowResize = () => {
        const swiperInstance = swiperRef.current?.swiper;
        swiperInstance.slides.forEach((slide, index) => {
            slide.firstChild.classList.forEach((item) => {
                if (item.includes('active')) {
                    swiperInstance.update(); // es fa l'update per actualitzar el snapGrid
                    handlePlaceSlider(index);
                }
            });
        });
    };

    const handleClickedSlide = () => {
        if (seccio !== seccioActual) {
            const swiperInstance = swiperRef.current?.swiper;
            swiperInstance.slides.forEach((slide, index) => {
                slide.firstChild.classList.forEach((item) => {
                    if (item.includes('active')) {
                        handlePlaceSlider(index);
                        setSeccioActual(seccio);
                    }
                });
            });
        }
    };

    const _handleFletxes = (swiper) => {
        if (swiper.allowSlideNext === true) {
            if (swiper.progress === 0) {
                setAllowSlidePrev(false);
                setAllowSlideNext(true);
            } else if (swiper.progress === 1) {
                setAllowSlidePrev(true);
                setAllowSlideNext(false);
            } else {
                setAllowSlidePrev(true);
                setAllowSlideNext(true);
            }
        } else {
            setAllowSlidePrev(false);
            setAllowSlideNext(false);
        }
    };

    const _navegacio = (forward) => {
        const swiperInstance = swiperRef.current?.swiper;
        if (swiperInstance) {
            let scrollOffset = 30;
            let realIndex = swiperInstance.snapGrid.findIndex(
                (snapPosition) => snapPosition >= Math.abs(swiperInstance.translate)
            );
            realIndex = forward ? realIndex + 1 : realIndex - 1;
            if (realIndex === -1) {
                realIndex = swiperInstance.snapGrid.length - 1;
            }

            if ((realIndex <= 1 && !forward) || (realIndex >= swiperInstance.snapGrid.length && forward)) {
                scrollOffset = 0;
            }

            let nextPosition;
            if (realIndex <= swiperInstance.snapGrid.length - 1) {
                nextPosition = swiperInstance.snapGrid[realIndex];
                if (spaceBetween && realIndex === swiperInstance.snapGrid.length - 1) {
                    scrollOffset = 0;
                }
            } else {
                nextPosition = swiperInstance.snapGrid[swiperInstance.snapGrid.length - 1];
            }
            swiperInstance.translateTo(nextPosition * -1 + scrollOffset, 500);
            swiperInstance.update();
        }
    };

    const _renderSwiper = () => {
        const BUTTON_ROLE = 'button, [role="button"]';

        const _slidesIndexOf = (swiper) => {
            if (!swiper || !swiper.slides) return;
            const slidesInSight = swiper.slides.filter((slide) => slide.classList.contains('swiper-slide-visible'));
            const lastVisibleSlideIndex = props.children.length - 1;
            const lastVisibleSlidePosition = swiper.slidesGrid[lastVisibleSlideIndex];
            const lastVisibleSlideWidth = swiper.slidesSizesGrid[lastVisibleSlideIndex];
            const lastVisibleSlideEnd = lastVisibleSlidePosition + lastVisibleSlideWidth;
            const isLastSlideOverFlowing = swiper.size - swiper.translate < lastVisibleSlideEnd;
            const firstVisibleSlideIndex = 0;
            const firstVisibleSlidePosition = swiper.slidesGrid[firstVisibleSlideIndex];
            const isFirstSlideOverFlowing = swiper.translate + firstVisibleSlidePosition < 0;

            slidesInSight.forEach((slide) => {
                if (slide.querySelectorAll('a').length > 0) {
                    slide.querySelectorAll('a').forEach((link) => {
                        if (slidesInSight.length === 2 && isFirstSlideOverFlowing && isLastSlideOverFlowing) {
                            if (slide === slidesInSight[0]) {
                                link.setAttribute('tabindex', '0');
                                slide.setAttribute('aria-hidden', 'false');
                            } else {
                                link.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            }
                        } else {
                            if (isLastSlideOverFlowing && slide === slidesInSight[slidesInSight.length - 1]) {
                                link.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            } else if (isFirstSlideOverFlowing && slide === slidesInSight[0]) {
                                link.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            } else {
                                link.setAttribute('tabindex', '0');
                                slide.setAttribute('aria-hidden', 'false');
                            }
                        }
                    });
                } else if (slide.querySelectorAll(BUTTON_ROLE).length > 0) {
                    slide.querySelectorAll(BUTTON_ROLE).forEach((button) => {
                        if (slidesInSight.length === 2 && isFirstSlideOverFlowing && isLastSlideOverFlowing) {
                            if (slide === slidesInSight[0]) {
                                button.setAttribute('tabindex', '0');
                                slide.setAttribute('aria-hidden', 'false');
                            } else {
                                button.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            }
                        } else {
                            if (isLastSlideOverFlowing && slide === slidesInSight[slidesInSight.length - 1]) {
                                button.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            } else if (isFirstSlideOverFlowing && slide === slidesInSight[0]) {
                                button.setAttribute('tabindex', '-1');
                                slide.setAttribute('aria-hidden', 'true');
                            } else {
                                button.setAttribute('tabindex', '0');
                                slide.setAttribute('aria-hidden', 'false');
                            }
                        }
                    });
                }
            });
            swiper.slides.forEach((slide) => {
                if (!slide.classList.contains('swiper-slide-visible')) {
                    if (slide.querySelectorAll('a').length > 0) {
                        slide.querySelectorAll('a').forEach((link) => {
                            link.setAttribute('tabindex', '-1');
                            slide.setAttribute('aria-hidden', 'true');
                        });
                    } else if (slide.querySelectorAll(BUTTON_ROLE).length > 0) {
                        slide.querySelectorAll(BUTTON_ROLE).forEach((button) => {
                            button.setAttribute('tabindex', '-1');
                            slide.setAttribute('aria-hidden', 'true');
                        });
                    }
                }
            });
        };
        return (
            <Swiper
                modules={[Navigation, FreeMode]}
                className={stylesSwiper}
                aria-live='polite'
                slidesPerView={'auto'}
                spaceBetween={spaceBetween ? spaceBetween : 0}
                freeMode={true}
                tag='nav'
                wrapperTag='ul'
                updateOnWindowResize={false}
                onSwiper={(swiper) => {
                    handleWindowResize();
                    _handleFletxes(swiper);
                }}
                ref={swiperRef}
                onAfterInit={(swiper) => {
                    _slidesIndexOf(swiper);
                }}
                onProgress={(swiper) => {
                    _handleFletxes(swiper);
                    setTimeout(() => {
                        _slidesIndexOf(swiper);
                    }, 0);
                }}
                data-testid={`slider-${sliderId}`}
                watchSlidesProgress={true}
            >
                {iterateItems(items)}
            </Swiper>
        );
    };

    return !__isEmpty(items) ? (
        <>
            {isJavaScriptClientEnabled ? (
                _renderSwiper()
            ) : (
                <Mosaic numColumns={items.length} numElementsDisplay={items.length} className={stylesMosaicNoJavascript}>
                    {items}
                </Mosaic>
            )}
            <nav className={stylesFletxes} aria-label='Carrusel menú'>
                <button onClick={() => _navegacio(false)} className={stylesButtonPrev}>
                    <div className={`${styles.anterior} ${styles.fletxesMenu}`} />
                    <Icona
                        icona='fletxa_esquerra'
                        isBlanc={false}
                        alt='Anterior'
                        mida={16}
                        inline={true}
                        className={stylesIconaFletxa}
                        //tabIndex={4}
                    />
                </button>
                <button onClick={() => _navegacio(true)} className={stylesButtonNext}>
                    <div className={`${styles.seguent} ${styles.fletxesMenu}`} />
                    <Icona
                        icona='fletxa_dreta'
                        isBlanc={false}
                        alt='Següent'
                        mida={16}
                        inline={true}
                        className={`${stylesIconaFletxa} ${styles.iconadreta}`}
                        //tabIndex={4}
                    />
                </button>
            </nav>
        </>
    ) : (
        <></>
    );
};

export default SliderMenu;
